import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { MapPin, Bed, Calendar, ArrowRight } from "lucide-react";
import { FadeUp, GoldLineDraw } from "@/components/motion/Reveal";

const STATUS_COLORS = {
  "Selling Now":            { bg: "rgba(37,211,102,0.14)", fg: "#0A7A2C" },
  "Off-Plan · Reserve Now": { bg: "rgba(212,175,55,0.20)", fg: "#8a6b12" },
  "Coming Soon":            { bg: "rgba(10,59,40,0.10)",   fg: "#0A3B28" },
};

function money(v, cur = "GBP") {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(v || 0);
}

export default function FeaturedProjectsSection() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/projects?featured=true");
        setItems(data);
      } catch {}
    })();
  }, []);

  if (!items.length) return null;

  return (
    <section className="py-24 md:py-32 relative" data-testid="featured-projects-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div className="max-w-2xl">
            <FadeUp><div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ New Projects</span></div></FadeUp>
            <FadeUp delay={0.05}>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-[54px] leading-[1.02] mv-green-text mt-3">
                Coming to life — <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>reserve before launch.</span>
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="mt-4 text-[15px] max-w-xl" style={{ color: "var(--mv-muted)" }}>
                Townhouses, apartment complexes and family estates across Southern Africa — pre-launch pricing for MAVULA members only.
              </p>
            </FadeUp>
            <FadeUp delay={0.15}><GoldLineDraw className="mt-5 w-14" /></FadeUp>
          </div>
          <FadeUp delay={0.1}>
            <Link to="/projects" className="btn-ghost" data-testid="featured-see-all">
              See all projects <ArrowRight size={14} />
            </Link>
          </FadeUp>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {items.slice(0, 3).map((p, i) => {
            const badge = STATUS_COLORS[p.status] || STATUS_COLORS["Coming Soon"];
            return (
              <FadeUp key={p.id} delay={i * 0.08}>
                <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.35 }} className="mv-card overflow-hidden h-full flex flex-col">
                  <Link to={`/projects/${p.slug || p.id}`} className="block relative h-56 overflow-hidden">
                    <img src={p.hero_image} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                    <span className="absolute top-3 left-3 text-[11px] px-2.5 py-1 rounded-full font-semibold" style={{ background: badge.bg, color: badge.fg }}>{p.status}</span>
                    <span className="absolute top-3 right-3 text-[11px] px-2.5 py-1 rounded-full font-semibold backdrop-blur-md text-white" style={{ background: "rgba(10,59,40,0.55)" }}>
                      {p.country_flag} {p.country}
                    </span>
                  </Link>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="text-[11px] tracking-[0.2em] uppercase" style={{ color: "var(--mv-muted)" }}>{p.type}</div>
                    <Link to={`/projects/${p.slug || p.id}`} className="font-display text-xl mv-green-text mt-1 hover:underline">{p.name}</Link>
                    <div className="mt-2 flex items-center gap-3 text-[13px]" style={{ color: "var(--mv-muted)" }}>
                      <span className="inline-flex items-center gap-1"><MapPin size={13} style={{ color: "var(--mv-gold)" }}/> {p.city}</span>
                      <span className="inline-flex items-center gap-1"><Bed size={13} style={{ color: "var(--mv-gold)" }}/> {p.bedrooms}</span>
                      <span className="inline-flex items-center gap-1"><Calendar size={13} style={{ color: "var(--mv-gold)" }}/> {p.handover}</span>
                    </div>
                    <div className="mt-4 flex items-end justify-between">
                      <div>
                        <div className="text-[10.5px] uppercase tracking-[0.2em]" style={{ color: "var(--mv-muted)" }}>From</div>
                        <div className="font-display text-[26px] mv-green-text leading-none tracking-tight">{money(p.price_from, p.currency)}</div>
                      </div>
                      <div className="text-right text-[12px]" style={{ color: "var(--mv-muted)" }}>{p.units_available} of {p.units_total} left</div>
                    </div>
                    <Link to={`/projects/${p.slug || p.id}`} className="btn-green mt-5 w-full justify-center !py-2" data-testid={`featured-view-${p.id}`}>
                      View Project <ArrowRight size={14}/>
                    </Link>
                  </div>
                </motion.div>
              </FadeUp>
            );
          })}
        </div>
      </div>
    </section>
  );
}
